import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardHeader } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { useBrandReport, useCostSummary, useVisibilityEngines } from '../../../../hooks/useVisibility';
import { useTriggerCitationSweep, useLatestCitationRun } from '../../../../hooks/useMeasurement';
import { useFilters } from '../filters';
import { formatDayMonth } from '../../../../utils/dateFormat';
import { PROVIDER_LABELS, PROVIDER_COLORS, PROVIDER_FALLBACK_COLOR, pct } from '../format';
import { CHART_AXIS, CHART_GRID, CHART_TOOLTIP, BAR_RADIUS_VERTICAL } from '../../../../components/ui/chart';
import type { MeasurementPage } from '../shell-types';
import { SweepStatusPill, isRunActive } from '../SweepStatusPill';
import { EmptyState, LoadingState } from '../../../../components/ui/States';

interface Props {
  onGoto: (page: MeasurementPage) => void;
}

export function EnginesPage({ onGoto }: Props) {
  const { startDate, endDate } = useFilters();
  const { data: engines } = useVisibilityEngines();
  const sweep = useTriggerCitationSweep();
  const latestRun = useLatestCitationRun();
  const run = latestRun.data?.run ?? null;
  const runActive = isRunActive(run?.status) || sweep.isPending;
  const engineRows = engines?.items ?? [];
  // Most engines never mention us; list the ones that do and fold the zeros into one line.
  const [zeroIds, setZeroIds] = useState<ReadonlySet<string>>(new Set());
  const [showZero, setShowZero] = useState(false);
  const reportZero = useCallback(
    (id: string, zero: boolean) =>
      setZeroIds((prev) => {
        if (prev.has(id) === zero) return prev;
        const next = new Set(prev);
        if (zero) next.add(id);
        else next.delete(id);
        return next;
      }),
    [],
  );

  return (
    <div className="space-y-5">
      <Card padding="md">
        <CardHeader
          title="Engines"
          description="Visibility per LLM engine over the selected range"
          action={
            <Button
              size="sm"
              variant="primary"
              onClick={() => sweep.mutate()}
              isLoading={sweep.isPending}
              disabled={runActive}
            >
              Run sweep now
            </Button>
          }
        />
        {(run || sweep.isError) && (
          <div className="mb-3">
            <SweepStatusPill run={run} triggerError={sweep.error} />
          </div>
        )}
        {engineRows.length === 0 ? (
          <p className="py-8 text-center text-body-sm text-text-tertiary">
            No engines configured yet. <button type="button" onClick={() => onGoto('profile')} className="text-info-dark hover:text-info underline-offset-2 hover:underline">Open Profile</button> to enable engines.
          </p>
        ) : (
          <div>
            {engineRows.map((engine) => (
              <EngineRow
                key={engine.id}
                engineId={engine.id}
                engineLabel={engine.description}
                provider={engine.provider}
                model={engine.currentModel}
                startDate={startDate}
                endDate={endDate}
                onGoto={onGoto}
                hidden={!showZero && zeroIds.has(engine.id)}
                onZero={reportZero}
              />
            ))}
            {zeroIds.size > 0 && (
              <div className="flex items-center justify-between pt-3 text-body-xs text-text-tertiary">
                <span>
                  {showZero ? `${zeroIds.size} at 0% shown` : `+${zeroIds.size} more at 0%`}
                </span>
                <span className="flex gap-3">
                  <button type="button" onClick={() => setShowZero((v) => !v)} className="text-accent hover:underline">
                    {showZero ? 'Hide them' : 'Show them'}
                  </button>
                  <button type="button" onClick={() => onGoto('prompts')} className="text-accent hover:underline">
                    Add coverage →
                  </button>
                </span>
              </div>
            )}
          </div>
        )}
      </Card>
      <CostByProvider />
    </div>
  );
}

function EngineRow({ engineId, engineLabel, provider, model, startDate, endDate, onGoto, hidden, onZero }: {
  engineId: string; engineLabel: string; provider: string; model: string; startDate: string; endDate: string; onGoto: (p: MeasurementPage) => void;
  hidden: boolean; onZero: (id: string, zero: boolean) => void;
}) {
  const { data } = useBrandReport(startDate, endDate, { engineIds: engineId, order_by: 'visibility' });
  const own = data?.rows.find((r) => r[data.columns.indexOf('is_own')] === 1);
  const visibility = (own?.[data!.columns.indexOf('visibility')] as number | null | undefined) ?? null;
  const isZero = visibility != null && visibility <= 0;
  useEffect(() => onZero(engineId, isZero), [engineId, isZero, onZero]);

  if (hidden) return null;
  return (
    <div className="flex items-center justify-between border-b border-border-light py-3 last:border-0">
      <div className="flex items-center gap-3">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PROVIDER_COLORS[provider] ?? PROVIDER_FALLBACK_COLOR }} />
        <div>
          <p className="font-medium text-body-md">{engineLabel}</p>
          <p className="text-body-xs text-text-tertiary">{PROVIDER_LABELS[provider] ?? provider} · {model}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-stat-sm tabular-nums">{pct(visibility)}</p>
        {isZero ? (
          <button
            type="button"
            onClick={() => onGoto('prompts')}
            className="text-body-xs text-accent hover:underline underline-offset-2"
          >
            Add coverage →
          </button>
        ) : (
          <p className="text-body-xs text-text-tertiary">visibility</p>
        )}
      </div>
    </div>
  );
}

function CostByProvider() {
  const { startDate, endDate } = useFilters();
  const { data, isLoading } = useCostSummary(startDate, endDate);
  const { rows, providers, total } = useMemo(() => {
    const items = data?.items ?? [];
    const dateMap = new Map<string, Record<string, number>>();
    const providerSet = new Set<string>();
    let sum = 0;
    for (const it of items) {
      providerSet.add(it.provider);
      sum += it.costUsd;
      const row = dateMap.get(it.date) ?? ({ date: it.date } as unknown as Record<string, number>);
      (row as Record<string, number>)[it.provider] = ((row as Record<string, number>)[it.provider] ?? 0) + it.costUsd;
      dateMap.set(it.date, row);
    }
    return {
      rows: Array.from(dateMap.values()).sort((a, b) => String((a as Record<string, unknown>).date).localeCompare(String((b as Record<string, unknown>).date))),
      providers: Array.from(providerSet),
      total: sum,
    };
  }, [data]);

  return (
    <Card padding="md">
      <CardHeader title="Cost by engine" description={`Total: $${total.toFixed(2)} across ${rows.length} day(s)`} />
      {isLoading ? <LoadingState /> : rows.length === 0 ? (
        <EmptyState compact title="No cost data captured yet." />
      ) : (
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="date" {...CHART_AXIS} tickFormatter={(d) => formatDayMonth(String(d))} />
              <YAxis {...CHART_AXIS} tickFormatter={(v) => `$${Number(v).toFixed(2)}`} />
              <Tooltip {...CHART_TOOLTIP} formatter={(v) => [`$${Number(v).toFixed(3)}`, '']} />
              {providers.map((p, i) => (
                <Bar key={p} dataKey={p} stackId="cost" fill={PROVIDER_COLORS[p] ?? PROVIDER_FALLBACK_COLOR} radius={i === providers.length - 1 ? [...BAR_RADIUS_VERTICAL] : undefined} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
